/**
 * What a retro-reflective number plate does to a camera that lights it itself.
 *
 * ── Where the numbers come from ─────────────────────────────────────────────
 *
 * THE PLATE. 3M, "High Definition Reflective License Plate Sheeting Series
 * 6750", Product Bulletin, October 2022, Table 1: typical minimum coefficient
 * of retroreflection R_A of WHITE sheeting, in candela per lux per square
 * metre, measured to ASTM E810, average of the 0 and 90 degree rotations.
 * Finished plates made to 3M's own recommendations meet the minima of
 * ISO 7591, which is the standard European plates are made to.
 *
 * THE SHUTTER. Axis, "License plate capture" white paper, table "Recommended
 * max shutter time depending on camera angle and car speed". Every cell of
 * that table is one rule: the plate may smear no more than about 14 mm ACROSS
 * the line of sight. Checked against four cells before it was used here, and
 * it holds to a tenth of a millimetre, so this carries the rule rather than a
 * copy of the grid, and answers for speeds and angles the grid does not list.
 *
 * THE READING. Same paper: at least 75 pixels across the plate for the
 * characters to have full contrast, and most plate-reading software asks for
 * 100 to 150. A total angle under 30 degrees is what Axis recommends.
 *
 * ── What is NOT here, and why ───────────────────────────────────────────────
 *
 * ⛔ NONE OF THIS IS INFRARED. R_A is photometric: candelas, weighted by the
 * eye, which is blind at the 850 nm an ANPR illuminator works at. No
 * manufacturer publishes the coefficient at that wavelength, and the visible
 * figure is the only one there is. White sheeting is microprismatic and
 * retroreflects by total internal reflection, which is close to
 * wavelength-neutral, so the visible figure is the best available stand-in and
 * it is labelled as one every time it leaves this module.
 *
 * ⛔ THE OBSERVATION ANGLE NEEDS THE CAMERA'S OWN GEOMETRY. It is the angle
 * between the illuminator and the lens as seen from the plate, so it needs the
 * separation between them, which datasheets do not print. `observationAngleDeg`
 * takes it as a measurement the caller must have; `plateGainRange` is for
 * callers that do not, and returns the band the table spans instead of
 * pretending to a point.
 */

/** 3M PB 6750, Table 1. Observation angle in degrees to entrance angle to R_A. */
export const PLATE_RA_TABLE: ReadonlyArray<{ observationDeg: number; entranceDeg: number; ra: number }> = [
  { observationDeg: 0.2, entranceDeg: -4, ra: 150 },
  { observationDeg: 0.2, entranceDeg: 30, ra: 26 },
  { observationDeg: 0.5, entranceDeg: -4, ra: 45 },
  { observationDeg: 0.5, entranceDeg: 30, ra: 13 },
  { observationDeg: 1.0, entranceDeg: -4, ra: 20 },
  { observationDeg: 1.0, entranceDeg: 30, ra: 7 },
];

const OBSERVATION_DEG = [0.2, 0.5, 1.0] as const;
const ENTRANCE_DEG = [-4, 30] as const;

/** Axis's rule, metres: the smear across the line of sight their whole shutter
 *  table holds constant. */
export const PLATE_SMEAR_BUDGET_M = 0.014;

/** Axis: the plate must cross at least this many pixels for the characters to
 *  have full contrast. */
export const PLATE_MIN_PIXELS = 75;
/** Axis: what most plate-reading software asks for, as a band. */
export const PLATE_SOFTWARE_PIXELS = { min: 100, max: 150 } as const;
/** Axis: recommended ceiling on the total angle between camera and plate. */
export const PLATE_MAX_TOTAL_ANGLE_DEG = 30;

/** Width of a European plate, metres (EU 520 x 110 mm single row). */
export const EU_PLATE_WIDTH_M = 0.52;

function lookup(observationDeg: number, entranceDeg: number): number {
  const row = PLATE_RA_TABLE.find(
    (r) => r.observationDeg === observationDeg && r.entranceDeg === entranceDeg,
  );
  if (!row) throw new Error(`no 3M cell at ${observationDeg} / ${entranceDeg}`);
  return row.ra;
}

/**
 * The sheeting's coefficient of retroreflection at an angle pair, cd/lx/m².
 *
 * Interpolated inside the table's own bounds and CLAMPED outside them. Below
 * 0.2 degrees a real sheeting climbs steeply and this holds it at the 0.2
 * value, which understates the plate; extrapolating a retroreflection curve
 * off the end of a manufacturer's table is exactly the kind of invention this
 * module exists to avoid. Observation is interpolated in the log, because R_A
 * falls by an order of magnitude across the table's three rows; entrance is
 * linear across its two.
 */
export function plateRetroreflection(observationDeg: number, entranceDeg: number): number {
  const o = Math.min(Math.max(observationDeg, OBSERVATION_DEG[0]), OBSERVATION_DEG[2]);
  const e = Math.min(Math.max(entranceDeg, ENTRANCE_DEG[0]), ENTRANCE_DEG[1]);
  const eT = (e - ENTRANCE_DEG[0]) / (ENTRANCE_DEG[1] - ENTRANCE_DEG[0]);
  const at = (obs: number) => {
    const lo = lookup(obs, ENTRANCE_DEG[0]);
    const hi = lookup(obs, ENTRANCE_DEG[1]);
    return lo + (hi - lo) * eT;
  };
  const i = o <= OBSERVATION_DEG[1] ? 0 : 1;
  const [oLo, oHi] = [OBSERVATION_DEG[i], OBSERVATION_DEG[i + 1]];
  const t = (o - oLo) / (oHi - oLo);
  const lo = at(oLo);
  const hi = at(oHi);
  return Math.exp(Math.log(lo) + (Math.log(hi) - Math.log(lo)) * t);
}

/**
 * The reflectance a plain diffuse surface would need to look as bright as the
 * plate does to this camera.
 *
 * A Lambertian surface of reflectance rho has a coefficient of rho / pi, so
 * the plate's equivalent is R_A x pi. That identity is what lets one number
 * stand beside the ordinary reflectances the rest of the lighting model uses:
 * a plate at 150 cd/lx/m² returns like a diffuse surface of reflectance 471,
 * which is why it clips while the car behind it stays black.
 */
export function plateLambertianEquivalent(ra: number): number {
  return ra * Math.PI;
}

/**
 * How many times more light the plate sends back than the surface beside it,
 * both lit by the same source from the same place.
 */
export function plateGainOverSurface(ra: number, surfaceReflectance: number): number {
  if (!(surfaceReflectance > 0)) return Infinity;
  return plateLambertianEquivalent(ra) / surfaceReflectance;
}

/**
 * The angle between the camera's illuminator and its lens, seen from the plate.
 *
 * `illuminatorOffsetM` is the separation between them on the housing, which is
 * a measurement of the specific camera and not a published figure.
 */
export function observationAngleDeg(illuminatorOffsetM: number, distanceM: number): number {
  if (!(distanceM > 0) || !(illuminatorOffsetM > 0)) return 0;
  return (Math.atan(illuminatorOffsetM / distanceM) * 180) / Math.PI;
}

/**
 * The band the table spans across the observation angles an ANPR install can
 * produce, for a caller that does not know the housing's own separation.
 *
 * Both ends carry the same verdict, which is why the band is useful rather
 * than merely honest: at either end the plate is three orders of magnitude
 * above the road.
 */
export function plateGainRange(entranceDeg: number): { min: number; max: number } {
  const wide = plateRetroreflection(OBSERVATION_DEG[2], entranceDeg);
  const tight = plateRetroreflection(OBSERVATION_DEG[0], entranceDeg);
  return { min: Math.min(wide, tight), max: Math.max(wide, tight) };
}

/**
 * The longest shutter that keeps a moving plate readable, seconds.
 *
 * `angleDeg` is the angle between the camera's axis and the vehicle's travel:
 * head-on is 0 and smears nothing across the frame, which is why a camera down
 * the lane can expose far longer than one across it. Infinite at exactly 0 by
 * the geometry, so the caller decides what to do with a perfectly axial shot.
 */
export function maxPlateShutterS(speedKmh: number, angleDeg: number): number {
  const v = (speedKmh * 1000) / 3600;
  const across = Math.abs(Math.sin((angleDeg * Math.PI) / 180));
  if (!(v > 0) || across <= 0) return Infinity;
  return PLATE_SMEAR_BUDGET_M / (v * across);
}

/** Pixels across a plate `distanceM` away, for a camera of `horizontalPixels`
 *  spanning `hfovDeg`. The same geometry the DORI ladder uses. */
export function platePixelsAcross(opts: {
  horizontalPixels: number;
  hfovDeg: number;
  distanceM: number;
  plateWidthM?: number;
}): number {
  const { horizontalPixels, hfovDeg, distanceM } = opts;
  if (!(horizontalPixels > 0) || !(hfovDeg > 0) || !(distanceM > 0)) return 0;
  const frameWidthM = 2 * distanceM * Math.tan((hfovDeg * Math.PI) / 360);
  if (!(frameWidthM > 0)) return 0;
  return ((opts.plateWidthM ?? EU_PLATE_WIDTH_M) / frameWidthM) * horizontalPixels;
}

export type PlateReadVerdict = 'unreadable' | 'contrast-only' | 'readable';

/**
 * Axis's own two thresholds, as a verdict: below 75 pixels the characters do
 * not have full contrast, and below what the software asks for they may image
 * correctly and still not be read.
 */
export function plateReadVerdict(pixelsAcross: number): PlateReadVerdict {
  if (pixelsAcross < PLATE_MIN_PIXELS) return 'unreadable';
  if (pixelsAcross < PLATE_SOFTWARE_PIXELS.min) return 'contrast-only';
  return 'readable';
}
